import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import type { Observable } from 'rxjs';
import type {
  AccountsResponse,
  ForgotPasswordRequest,
  IdentifyResponse,
  MfaSendResponse,
  MfaVerifyResponse,
  PasswordResponse,
  SelectAccountResponse,
} from '@core/models/sso.models';

/**
 * The eight calls the sign-in flow makes, in the order it makes them.
 *
 * ---------------------------------------------------------------------------------------------
 * THE SEQUENCE IS THE PROTOTYPE'S, VERBATIM. `Accounts_SSO_Login_Prototype.html` logs every call it
 * would make through a Flow Inspector, and that log is the contract this file implements:
 *
 *   GET  /v1/sso/authorize?app=&redirect_uri=&state=&code_challenge=&code_challenge_method=
 *   POST /v1/sso/identify        { identifier }
 *   POST /v1/sso/password        { identity_ref, password }
 *   POST /v1/sso/mfa/send        { mfa_token, channel }
 *   POST /v1/sso/mfa/verify      { mfa_token, code, remember_device, device_fingerprint }
 *   GET  /v1/sso/accounts?app=
 *   POST /v1/sso/select-account  { merchant_id, app }
 *   POST /v1/sso/token           { grant_type, code, … }
 *   POST /v1/sso/password/forgot { identifier }
 *   POST /v1/sso/password/reset  { identifier, code, new_password }
 *
 * Two deliberate departures from that list, both because the prototype is a design artefact and the
 * service is the authority:
 *
 *   the base is `/api/v1/sso`, not `/v1/sso` -- the service registers with `{prefix: "api/v1"}`;
 *   `req` is threaded through identify / password / select-account, which the prototype elides
 *   because it fakes the redirect rather than performing it.
 *
 * Everything else -- every field name, every method, every parameter -- matches. Where an earlier
 * version of this file disagreed, it was wrong: it sent `app_code` for `app`, a `flow_id` that does
 * not exist for `identity_ref` / `mfa_token` / `selection_token`, `trust_device` for
 * `remember_device`, `password` for `new_password`, and `?flow_id=` for `?app=`.
 * ---------------------------------------------------------------------------------------------
 *
 * Transport only. It decides nothing, holds no state, and interprets no error.
 */
@Injectable({ providedIn: 'root' })
export class SsoApi {
  private readonly http = inject(HttpClient);

  /**
   * gozem-api-accounts-service registers its route table with `{prefix: "api/v1"}` (src/app.js), so
   * the `api` segment is not optional -- without it every call answers
   * `404 Route POST:/v1/sso/... not found`.
   */
  private readonly base = `${environment.accountsApiUrl}/api/v1/sso`;

  /**
   * `GET /authorize` is NOT called from here.
   *
   * It answers 302 to our own login page, so the browser performs it and the `req` handle arrives as
   * a query parameter. Calling it with `HttpClient` would follow the redirect invisibly and throw
   * the handle away. `buildAuthorizeUrl` exists so the URL is still constructed in one place.
   */
  buildAuthorizeUrl(query: {
    app: string;
    redirectUri: string;
    state: string;
    codeChallenge: string;
  }): string {
    const params = new URLSearchParams({
      app: query.app,
      redirect_uri: query.redirectUri,
      state: query.state,
      code_challenge: query.codeChallenge,
      code_challenge_method: 'S256',
    });
    return `${this.base}/authorize?${params.toString()}`;
  }

  identify(payload: {
    identifier: string;
    app: string;
    req: string;
  }): Observable<IdentifyResponse> {
    return this.http.post<IdentifyResponse>(
      `${this.base}/identify`,
      { identifier: payload.identifier, app: payload.app, req: payload.req },
      { withCredentials: true }
    );
  }

  /**
   * The FIRST factor. Never returns a token — only an `mfa_token` or a `selection_token`.
   *
   * `device_fingerprint` is what binds a trusted device: the trust record carries a hash of it, and
   * without it a device that IS trusted is not recognised and the user is sent through the OTP step
   * anyway.
   */
  submitPassword(payload: {
    identityRef: string;
    password: string;
    deviceFingerprint: string;
    req: string;
  }): Observable<PasswordResponse> {
    return this.http.post<PasswordResponse>(
      `${this.base}/password`,
      {
        identity_ref: payload.identityRef,
        password: payload.password,
        device_fingerprint: payload.deviceFingerprint,
        req: payload.req,
      },
      { withCredentials: true }
    );
  }

  /** `channel` is REQUIRED by the schema — there is no server-side default. */
  sendOtp(payload: {
    mfaToken: string;
    channel: 'sms' | 'email' | 'whatsapp';
  }): Observable<MfaSendResponse> {
    return this.http.post<MfaSendResponse>(
      `${this.base}/mfa/send`,
      { mfa_token: payload.mfaToken, channel: payload.channel },
      { withCredentials: true }
    );
  }

  /**
   * The SECOND factor. `remember_device` skips the code step next time, and nothing else.
   *
   * `device_fingerprint` travels WITH the flag, and only with it. The issuer mints a trusted device
   * on `remember_device === true && device_fingerprint` — both, because trust is BOUND to a device
   * and trust that binds to nothing is just a longer-lived cookie. Send the flag alone and the
   * issuer silently mints nothing: the checkbox appears to work, the next sign-in asks for a code
   * anyway, and there is no error anywhere to explain it. Sending the fingerprint only when the
   * user asked to be remembered keeps it off every other request.
   */
  verifyOtp(payload: {
    mfaToken: string;
    code: string;
    rememberDevice: boolean;
    deviceFingerprint: string;
  }): Observable<MfaVerifyResponse> {
    return this.http.post<MfaVerifyResponse>(
      `${this.base}/mfa/verify`,
      {
        mfa_token: payload.mfaToken,
        code: payload.code,
        remember_device: payload.rememberDevice,
        ...(payload.rememberDevice ? { device_fingerprint: payload.deviceFingerprint } : {}),
      },
      { withCredentials: true }
    );
  }

  /** Keyed on the APPLICATION, not on a flow handle: eligibility is per application. */
  accounts(payload: { app: string; selectionToken: string }): Observable<AccountsResponse> {
    return this.http.get<AccountsResponse>(`${this.base}/accounts`, {
      params: { app: payload.app },
      headers: { authorization: `Bearer ${payload.selectionToken}` },
      withCredentials: true,
    });
  }

  selectAccount(payload: {
    merchantId: string;
    selectionToken: string;
    app: string;
    req: string;
  }): Observable<SelectAccountResponse> {
    return this.http.post<SelectAccountResponse>(
      `${this.base}/select-account`,
      {
        merchant_id: payload.merchantId,
        selection_token: payload.selectionToken,
        app: payload.app,
        req: payload.req,
      },
      { withCredentials: true }
    );
  }

  /**
   * 202 unconditionally, whether or not the identifier exists, and it issues no token.
   *
   * The caller must NOT branch on the outcome — see the forgot-password page. Branching rebuilds the
   * account-enumeration oracle the 202 exists to remove.
   */
  forgotPassword(payload: ForgotPasswordRequest): Observable<void> {
    return this.http.post<void>(`${this.base}/password/forgot`, payload);
  }

  /**
   * A CODE AND AN IDENTIFIER, not a link's token (GPGT-41342).
   *
   * `identifier` is not a convenience field: the code is six digits, so the service looks it up as
   * `{identity_id, code_hash}` and never by hash alone. Without the scope one guess would be tested
   * against every account at once. Sending only the code now answers `invalid_token`.
   *
   * The field is `new_password`. `password` is silently ignored by the schema.
   */
  resetPassword(payload: {
    identifier: string;
    code: string;
    newPassword: string;
  }): Observable<void> {
    return this.http.post<void>(`${this.base}/password/reset`, {
      identifier: payload.identifier,
      code: payload.code,
      new_password: payload.newPassword,
    });
  }
}
